import { useCallback, useMemo } from "react";
import { axiosClient } from "../config";
import { IApiClient, RequestConfig, RequestOptions } from "../api";
import { useAuth } from "../contexts";
import { AxiosResponse } from "axios";

export function useAxios(): IApiClient {
    const { accessToken, isAuthenticated } = useAuth();

    const buildConfig = useCallback(
        (config?: RequestConfig): RequestConfig => {
            const builder = { ...config };
            if (isAuthenticated && (!builder.headers || !("Authorization" in builder.headers))) {
                builder.headers = {
                    ...builder.headers,
                    Authorization: `Bearer ${accessToken}`,
                };
            }
            return builder;
        },
        [accessToken, isAuthenticated]
    );

    const get = useCallback(
        async <TRes>({ url, config }: RequestOptions): Promise<TRes> => {
            const res = await axiosClient.get<TRes>(url, buildConfig(config));
            return res.data;
        },
        [buildConfig]
    );

    const getBlob = useCallback(
        async ({ url, config }: RequestOptions): Promise<Blob> => {
            const res = await axiosClient.get<Blob>(url, {
                ...buildConfig(config),
                responseType: "blob",
            });
            return res.data;
        },
        [buildConfig]
    );

    const post = useCallback(
        async <TReq, TRes = TReq>({ url, data, config }: RequestOptions<TReq>): Promise<TRes> => {
            const res = await axiosClient.post<TRes>(url, data, buildConfig(config));
            return res.data;
        },
        [buildConfig]
    );

    const postWithFullResponse = useCallback(
        async <TReq, TRes = TReq>({ url, data, config }: RequestOptions<TReq>): Promise<AxiosResponse<TRes>> => {
            const res = await axiosClient.post<TRes>(url, data, buildConfig(config));
            return res;
        },
        [buildConfig]
    );

    const patch = useCallback(
        async <TReq, TRes = TReq>({ url, data, config }: RequestOptions<TReq>): Promise<TRes> => {
            const res = await axiosClient.patch<TRes>(url, data, buildConfig(config));
            return res.data;
        },
        [buildConfig]
    );

    const put = useCallback(
        async <TReq, TRes = TReq>({ url, data, config }: RequestOptions<TReq>): Promise<TRes> => {
            const res = await axiosClient.put<TRes>(url, data, buildConfig(config));
            return res.data;
        },
        [buildConfig]
    );

    const del = useCallback(
        async ({ url, config }: RequestOptions): Promise<boolean> => {
            const res = await axiosClient.delete(url, buildConfig(config));
            return res.status === 204;
        },
        [buildConfig]
    );

    const deleteWithContent = useCallback(
        async <TRes>({ url, config }: RequestOptions): Promise<TRes> => {
            const res = await axiosClient.delete(url, buildConfig(config));
            return res.data;
        },
        [buildConfig]
    );

    return useMemo(
        () => ({
            get,
            getBlob,
            post,
            postWithFullResponse,
            patch,
            put,
            del,
            deleteWithContent,
        }),
        [get, getBlob, post, postWithFullResponse, patch, put, del, deleteWithContent]
    );
}
